import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { TrendingUp, Clock, Star, Filter, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { PostCard } from "@/components/posts/PostCard"
import { Sidebar } from "@/components/layout/Sidebar"
import { getPosts, getPostsWithAuthors, enrichPostsWithTags, getCategories } from "@/lib/db"
import type { PostWithAuthor, Category } from "@/types"

export default function ExplorePage() {
  const [trending, setTrending] = useState<PostWithAuthor[]>([])
  const [latest, setLatest] = useState<PostWithAuthor[]>([])
  const [top, setTop] = useState<PostWithAuthor[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      try {
        const [trendingResult, latestResult, topResult, dbCategories] = await Promise.all([
          getPosts({ status: "published", orderByField: "upvote_count", orderDirection: "desc", limitCount: 20 }),
          getPosts({ status: "published", orderByField: "published_at", orderDirection: "desc", limitCount: 20 }),
          getPosts({ status: "published", orderByField: "view_count", orderDirection: "desc", limitCount: 20 }),
          getCategories(),
        ])

        const [trendingWithAuthors, latestWithAuthors, topWithAuthors] = await Promise.all([
          getPostsWithAuthors(trendingResult.posts).then(enrichPostsWithTags),
          getPostsWithAuthors(latestResult.posts).then(enrichPostsWithTags),
          getPostsWithAuthors(topResult.posts).then(enrichPostsWithTags),
        ])

        setTrending(trendingWithAuthors)
        setLatest(latestWithAuthors)
        setTop(topWithAuthors)
        setCategories([{ id: "all", name: "All", slug: "all", description: "", icon: "", color: "", postCount: 0, order: 0 }, ...dbCategories])
      } catch (err) {
        console.error("Failed to load explore data:", err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Explore</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Discover the latest articles and discussions in the React Native community.</p>
      </div>

      {/* Categories */}
      <div className="mb-6 flex flex-wrap gap-2">
        {categories.map(category => (
          <Link key={category.id} to={category.slug === "all" ? "/explore" : `/category/${category.slug}`}>
            <Badge variant={category.slug === "all" ? "default" : "secondary"} className="cursor-pointer">
              {category.name}
            </Badge>
          </Link>
        ))}
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        {/* Posts Feed */}
        <div className="flex-1">
          <Tabs defaultValue="trending" className="w-full">
            <div className="mb-6 flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="trending" className="flex items-center">
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Trending
                </TabsTrigger>
                <TabsTrigger value="latest" className="flex items-center">
                  <Clock className="mr-2 h-4 w-4" />
                  Latest
                </TabsTrigger>
                <TabsTrigger value="top" className="flex items-center">
                  <Star className="mr-2 h-4 w-4" />
                  Top
                </TabsTrigger>
              </TabsList>
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filters
              </Button>
            </div>

            <TabsContent value="trending" className="space-y-6">
              {trending.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </TabsContent>

            <TabsContent value="latest" className="space-y-6">
              {latest.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </TabsContent>

            <TabsContent value="top" className="space-y-6">
              {top.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </TabsContent>
          </Tabs>

          <div className="mt-8 text-center">
            <Button variant="outline" size="lg">
              Load more articles
            </Button>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="w-full lg:w-80">
          <Sidebar />
        </aside>
      </div>
    </div>
  )
}
